import SEO from "@/components/SEO";
import { ASSETS } from "@/lib/api";
import { CheckCircle2, ArrowRight, Check, X } from "lucide-react";
import WaitlistForm from "@/components/WaitlistForm";

export default function Network() {
    return (
        <main data-testid="network-page" className="text-white pt-32 pb-24">
            <SEO
                title="The Purposeful Network | Community for Impact-Driven Founders"
                description="A private, invite-only community for founders building businesses with meaningful impact. Curated peer roundtables, operator masterclasses, and the rooms that won't be on a stage."
                path="/network"
                keywords="Purposeful Network, founder community, impact founder network, peer roundtable, community for social entrepreneurs, leadership platform"
                schema={[
                    {
                        "@context": "https://schema.org",
                        "@type": "Organization",
                        "@id": "https://purposeful.inc/network#organization",
                        "name": "The Purposeful Network",
                        "url": "https://purposeful.inc/network",
                        "parentOrganization": { "@id": "https://purposeful.inc/#organization" },
                        "description": "A private community for founders committed to creating meaningful impact."
                    },
                    {
                        "@context": "https://schema.org",
                        "@type": "Service",
                        "serviceType": "Founder community and leadership platform",
                        "provider": { "@id": "https://purposeful.inc/#organization" },
                        "areaServed": "Global",
                        "audience": { "@type": "BusinessAudience", "audienceType": "Founders and CEOs of impact enterprises" }
                    }
                ]}
                breadcrumbs={[{ name: "Home", path: "/" }, { name: "Network", path: "/network" }]}
            />

            <section className="container-x mx-auto max-w-7xl">
                <div className="grid lg:grid-cols-2 gap-16 items-center">
                    <div>
                        <div className="overline mb-4">The Purposeful Network</div>
                <h1 className="font-display text-5xl md:text-6xl font-semibold tracking-tight">
                    A curated space for<br />
                    <span className="text-[#D4AF37]">founders who truly care.</span>
                </h1>
                        <p className="mt-6 text-zinc-300 text-lg leading-relaxed">
                            A private room for founders building businesses
                            with meaning. Curated conversations. Warm
                            introductions. The rooms that don't happen on
                            stage.
                        </p>
                        <div className="mt-8 max-w-md">
                            <WaitlistForm source="network-page" testIdPrefix="network-waitlist" submitLabel="Join the Network" compact />
                            <p className="mt-3 text-xs text-zinc-500 font-mono uppercase tracking-widest">
                                By invitation. Members are introduced from the waitlist.
                            </p>
                        </div>
                    </div>

                    <div className="relative">
                        <div className="absolute -inset-4 bg-gradient-to-tr from-[#D4AF37]/20 via-transparent to-transparent blur-3xl" />
                        <img src={ASSETS.network} alt="Founders" loading="lazy" decoding="async" className="relative w-full rounded-2xl ring-1 ring-white/10 object-cover aspect-[4/5]" data-testid="network-image" />
                    </div>
                </div>
            </section>

            <section className="container-x mx-auto max-w-7xl mt-32">
                <div className="grid md:grid-cols-3 gap-6">
                    {[
                        { t: "Curated peer roundtables", d: "Small, off-the-record gatherings with founders facing the same edge." },
                        { t: "Operator masterclasses", d: "Tactical sessions from founders who've shipped the hard thing." },
                        { t: "1:1 introductions", d: "Warm intros across capital, talent, partnership. When it actually matters." },
                        { t: "Annual offsite", d: "A weekend with the people building what's worth building." },
                        { t: "Founder-only Community", d: "Quiet, dense, generous. A signal-to-noise rare on the internet." },
                        { t: "Book club + early access", d: "Read Purposeful, Inc. with the founders inside it." },
                    ].map((c, i) => (
                        <div key={i} className="card-dark p-8" data-testid={`network-feature-${i}`}>
                            <CheckCircle2 size={18} className="text-[#D4AF37]" />
                            <h3 className="font-display text-xl mt-4">{c.t}</h3>
                            <p className="text-zinc-400 mt-2 text-sm leading-relaxed">{c.d}</p>
                        </div>
                    ))}
                </div>
            </section>

            <section className="container-x mx-auto max-w-6xl mt-24">
                <div className="grid lg:grid-cols-5 gap-12 items-start">
                    <div className="lg:col-span-2">
                        <div className="overline mb-4">Who it's for</div>
                        <h2 className="font-display text-4xl md:text-5xl font-semibold tracking-tight leading-tight">
                            Not for<br />
                            everyone.<br />
                            <span className="text-[#D4AF37]">On purpose.</span>
                        </h2>
                        <p className="mt-6 text-zinc-400 leading-relaxed">
                            The Network is intentionally small. We select for
                            character and conviction over stage and title.
                            Read the two lists carefully.
                        </p>
                    </div>

                    <div className="lg:col-span-3 grid md:grid-cols-2 gap-6">
                        <div className="card-dark p-8" data-testid="network-fit-yes">
                            <div className="overline text-[#D4AF37]">This is for you if</div>
                            <ul className="mt-5 space-y-4">
                                {[
                                    "You're building a venture where impact and business model are inseparable.",
                                    "You're between angel and Series B, or leading a growth-stage impact enterprise.",
                                    "You'd rather be in a room of 12 than on a panel of 1200.",
                                    "You'll show up when asked, and ask when you need.",
                                ].map((t, i) => (
                                    <li key={i} className="flex gap-3 text-sm text-zinc-200">
                                        <Check size={16} className="text-[#D4AF37] shrink-0 mt-0.5" />
                                        <span className="leading-relaxed">{t}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div className="rounded-xl border border-white/5 p-8 bg-black/40" data-testid="network-fit-no">
                            <div className="overline text-zinc-500">This isn't for you if</div>
                            <ul className="mt-5 space-y-4">
                                {[
                                    "You're looking for a directory of investors or a lead-gen network.",
                                    "You want to promote, pitch, or broadcast.",
                                    "You believe impact is a brand story, not a business decision.",
                                    "You'd rather be told what to think than think out loud with peers.",
                                ].map((t, i) => (
                                    <li key={i} className="flex gap-3 text-sm text-zinc-500">
                                        <X size={16} className="text-zinc-600 shrink-0 mt-0.5" />
                                        <span className="leading-relaxed">{t}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            <section className="section-y border-t border-white/5 relative overflow-hidden mt-16">
                <div className="absolute inset-0 fingerprint-bg" />
                <div className="container-x mx-auto max-w-3xl text-center relative">
                    <div className="overline mb-4">Introduce yourself</div>
                    <h2 className="font-display text-4xl md:text-6xl font-semibold tracking-tight">
                        Doors open<br />
                        <span className="text-[#D4AF37]">once a season.</span>
                    </h2>
                    <p className="mt-6 text-zinc-400 text-lg">
                        We introduce a small cohort of founders each quarter.
                        Add yourself and we'll be in touch when the next
                        window opens.
                    </p>
                    <div className="mt-8 max-w-lg mx-auto">
                        <WaitlistForm source="network-final-cta" testIdPrefix="network-final-waitlist" submitLabel="Join the Network" compact />
                    </div>
                </div>
            </section>
        </main>
    );
}
